import { useState } from 'react';
import { Check, Copy, Send } from 'lucide-react';
import { db } from '@/lib/db';
import type { Member } from '@/lib/db/types';
import { Sheet } from '@/components/shared/Sheet';
import { Crest } from '@/components/shared/Crest';

interface Props {
  member: Member;
  onClose: () => void;
}

export function InviteMemberModal({ member, onClose }: Props) {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimLink, setClaimLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const firstName = member.name.split(' ')[0] || 'A member';
  const defaultMsg = `${firstName} invited you to join Bag Tag — a private app for golfers at private country clubs to post rounds, settle wagers, and run our skins games. Tap the link to set up your profile.`;

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Enter their email so we can send them a sign-in link.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await db.invitations.send({
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        recipient_name: name.trim() || undefined,
        personal_note: note.trim() || undefined,
      });
      setClaimLink(result.claim_link);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!claimLink) return;
    try {
      await navigator.clipboard.writeText(claimLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.getElementById('invite-claim-link') as HTMLInputElement | null;
      input?.select();
      document.execCommand('copy');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Sheet title={claimLink ? 'Invitation Sent' : 'Invite a Member'} onClose={onClose}>
      <div className="px-4 pb-6 pt-2">
        {claimLink ? (
          <div className="space-y-4">
            <div className="py-6 text-center">
              <div className="mx-auto mb-4 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-good text-paper">
                <Check size={32} strokeWidth={3} />
              </div>
              <div className="font-serif text-[22px] font-bold tracking-[-0.02em] text-navy">Invite created</div>
              <div className="mt-1.5 text-[13px] text-ink-soft">
                Copy the link below and send it to <strong>{name.trim() || email}</strong>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                id="invite-claim-link"
                readOnly
                value={claimLink}
                onFocus={(e) => e.target.select()}
                className="min-w-0 flex-1 select-all rounded-[10px] border border-line bg-cream px-3 py-2.5 text-xs text-navy outline-none"
              />
              <button
                type="button"
                onClick={() => void copyLink()}
                className="flex shrink-0 items-center gap-1.5 rounded-[10px] bg-navy px-4 py-2.5 text-xs font-bold text-cream"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <button type="button" onClick={onClose} className="w-full rounded-xl bg-navy py-3.5 text-sm font-bold text-paper">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="mb-[18px] flex items-center gap-3 rounded-[14px] bg-navy p-[18px] text-cream shadow-bt">
              <Crest size={48} variant="dark" />
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-[0.15em] text-gold-bright">INVITE-ONLY · EMAIL INVITE</div>
                <div className="mt-1 font-serif text-base font-bold leading-tight tracking-[-0.015em]">
                  Vouch for a fellow ANCC golfer.
                </div>
              </div>
            </div>

            <div className="mb-[18px]">
              <div className="mb-2 text-[13px] font-semibold tracking-[-0.005em] text-ink-soft">Their email</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="friend@example.com"
                className="w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-[15px] text-ink outline-none"
              />
              <div className="mt-1.5 text-[11px] text-mute">We&apos;ll send them a secure invite link via email.</div>
            </div>

            <div className="mb-[18px]">
              <div className="mb-2 text-[13px] font-semibold tracking-[-0.005em] text-ink-soft">Phone (optional)</div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-[15px] text-ink outline-none"
              />
            </div>

            <div className="mb-[18px]">
              <div className="mb-2 text-[13px] font-semibold tracking-[-0.005em] text-ink-soft">Name (optional)</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Tom Hartwell"
                className="w-full rounded-[10px] border border-line bg-cream px-3.5 py-3 text-[15px] text-ink outline-none"
              />
            </div>

            <div className="mb-[18px]">
              <div className="mb-2 text-[13px] font-semibold tracking-[-0.005em] text-ink-soft">Personal note (optional)</div>
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={`Add a personal touch. We'll include a default text from ${firstName}.`}
                className="w-full resize-none rounded-[10px] border border-line bg-cream px-3.5 py-3 text-sm text-ink outline-none"
              />
            </div>

            <div className="mb-4 rounded-xl border border-gold bg-gold/10 p-3 text-xs leading-[1.5] text-ink-soft">
              <div className="mb-1 text-[10px] font-bold tracking-wide text-gold-deep">EMAIL PREVIEW</div>
              <div className="rounded-lg bg-paper p-2.5 text-xs leading-snug text-ink">
                &ldquo;{note.trim() || defaultMsg}&rdquo;
                <br />
                <span className="font-semibold text-navy">eagle.ancc.org/join/…</span>
              </div>
              <div className="mt-2 text-[10px] text-mute">
                Valid for 14 days. They&apos;ll complete onboarding (avatar, Venmo, handicap) before joining.
              </div>
            </div>

            {error && <div className="mb-3 rounded-xl bg-bad/10 p-3 text-sm text-bad">{error}</div>}

            <button
              type="button"
              disabled={submitting || !email.trim()}
              onClick={() => void handleSubmit()}
              className={`flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold ${
                email.trim() ? 'bg-navy text-paper' : 'bg-line text-mute'
              } disabled:opacity-60`}
            >
              <Send size={15} />
              {submitting ? 'Sending…' : 'Send Invite'}
            </button>
          </>
        )}
      </div>
    </Sheet>
  );
}
